<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :hasBack="false"
  >

    <template #layoutContent>
      <ActionsBar :query="query" :handleRangeDate="handleRangeDate" :contacts="contacts" :status="estimateStatus" id="estimate-datepicker"/>

      <Card :title="$t('estimate.list')" containerClass="mb-5 mt-5">
        <template #cardToolbar>
          <div class="d-flex justify-content-end align-items-center">
            <div>
              <Button v-if="$can(abilities.CREATE_ESTIMATE)" type="new" btn-class="btn-sm me-2" btn-text="estimate.create" @click="openCreateView" ></Button>
            </div>
          </div>
        </template>
        <template #cardBody>
          <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                   id="estimates_datatable">
              <thead class="mb-5">
                <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                  <th class="min-w-100px text-center">{{ $t('estimate.label.number') }}</th>
                  <th class="min-w-100px text-center">{{ $t('estimate.label.date') }}</th>
                  <th class="min-w-100px text-center">{{ $t('estimate.label.customer') }}</th>
                  <th class="min-w-100px text-center">{{ $t('estimate.label.status') }} </th>
                  <th class="min-w-100px text-center">{{ $t('estimate.label.totalAmount') }}</th>
                  <th class="w-100px text-start">{{ $t('label.action') }}</th>
                </tr>
              </thead>
          </table>
        </template>
      </Card>
    </template>
  </SubLayout>
</template>

<script setup>

import {computed, nextTick, onMounted, onUpdated, ref, watch} from "vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {formattedLocalAmount} from "@/mixins/Helpers";
import {useRouter} from "vue-router";
import {useLocales} from "@/composables/locales";
import {useHasAbility} from "@/composables/hasAbility";
import Card from "@/Modules/CRM/components/Card";
import Button from "@/components/Button.vue";
import ActionsBar from "@/Modules/CRM/views/Estimate/components/ActionsBar.vue";
import {useSwal} from "@/composables/useSwal";
import abilities from "@/composables/abilities";
import {Theme} from "@/composables/theming";

const router = useRouter()
const store = useStore()
const { can } = useHasAbility()
const {locale, datatableLocaleUrl} = useLocales()

const {showSwal} = useSwal()
const title = ref("breadcrumbs.estimate.title")
const breadcrumbs = ref([
{
  name: 'breadcrumbs.estimate.list',
  route: 'estimates'
}
])

const table = ref()
const query = ref({
  status: 'all',
  contact: 'all',
  start_date: '',
  end_date: ''
})

const estimateStatus = computed(() => store.getters['estimates/estimateStatus'])
const contacts = computed(() => store.getters['contacts/getContacts'])

watch(() => locale.value, (value) => {
  nextTick(() => fetchDatas())
}, { deep: true })

watch(() => query.value.status, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/estimates/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

watch(() => query.value.contact, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/estimates/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

watch(() => [query.value.start_date, query.value.end_date], (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/estimates/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#estimates_datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 1, 'desc']],
    ajax: {
      url: BASE_URL + `/get/estimates/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
      { data: "estimate_number" }, { data: "date" }, { data: "contact" }, { data: "status" }, { data: "total" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [

      {
        targets: -6,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        targets: -3,
        orderable: false,
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          let status = getEstimateStatusName(t)
          const locale = i18n.global.locale.value
          let color = status.color
          status = status[locale]
          return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
        },
      },

      {
        targets: -2,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + formattedLocalAmount(t) + ' ' + e.currency + "</span>";
        },
      },

      {
        width: "15%",
        targets: -1,
        "className": "dt-center text-start",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          if(can(abilities.VIEW_ESTIMATE) || can(abilities.VIEW_ALL_ESTIMATE)){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
          }
          if((can(abilities.UPDATE_ESTIMATE) || can(abilities.UPDATE_ALL_ESTIMATE)) && (e.status == 'DRAFT'  || e.status == 'PENDING')) {
              render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon edit_btn" title="'+ i18n.global.t('button.edit') +'"><i  data-id="'+ e.id +'" class="fas fa-edit fs-2"  style="color: '+Theme.gray +'"></i></a>'
          }
          if((can(abilities.DELETE_ESTIMATE) || can(abilities.DELETE_ALL_ESTIMATE)) && e.status == 'DRAFT'){
            render += '<a href="javascript:;" data-row="'+ l.row +'" data-id="'+ e.id +'" class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i data-row="'+ l.row +'" data-id="'+ e.id +'" class="fas fa-trash fs-2" style="color: '+Theme.gray +'"></i></a>'
          }

          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("click", "#estimates_datatable .show_btn", (e) => {
    router.push({ name: 'estimates-view', params: { id: e.target.getAttribute('data-id') } })
  });

  $(document).on("click", "#estimates_datatable .edit_btn", (e) => {
    router.push({ name: 'estimates-edit', params: { id: e.target.getAttribute('data-id') } })
  });

  $(document).on("click", "#estimates_datatable .delete_btn", (e) => {
    deleteEstimate(e.target.getAttribute('data-id'))
  });
}

function handleRangeDate (range) {
  query.value.start_date = range.start_date
  query.value.end_date = range.end_date
}

function getEstimateStatusName(status) {
  const {color, fr, en} = estimateStatus.value.find(el => el.value === status) || ''
  return {color, fr, en}
}

function openCreateView(){
  router.push({ name: 'estimates-create' })
}

function deleteEstimate(id){
  showSwal(
        i18n.global.t('crm.estimate.swal.deleted'),
        i18n.global.t('crm.estimate.swal.deleted_text')
    ).then( async (result) => {
        if (result.value) {
            store.dispatch('estimates/deleteEstimate', id)
                .then(() => {
                    window.toastr['success'](i18n.global.t('notification.estimate_deleted'))
                    table.value.ajax.reload()
                    closeModal()
                })
                .catch(() => {
                    
                })
        }
    })
}

function loadData() {
Promise.all([
  store.dispatch('estimates/loadEstimateStatus'),
  store.dispatch('contacts/loadContacts'),
  fetchDatas()
])
}

onMounted( () => {
loadData()
})


</script>
